import React, { useState } from 'react';
import { Tag, EmptyState, TYPE, cn } from '../ui';
import { PlannedPaymentDraft } from '../../utils/finance/planMath';
import { toDayKey } from '../../utils/finance/dates';
import { usd } from './planPayload';

interface PlanScheduleTableProps {
  payments: PlannedPaymentDraft[];
  /** Day keys that breach the low-balance threshold, for row highlighting. */
  breachDayKeys?: Set<string>;
  /** 'YYYY-MM' → lowest projected checking balance that month. Shown beside
   *  each payment so the cost of a month sits next to the month causing it. */
  floorByMonth?: Map<string, number>;
  threshold?: number;
  onOverride: (dayKey: string, amount: number | null) => void;
}

/**
 * Every payment in the plan, with inline per-month editing.
 *
 * Editing one month re-solves the tail — the payoff date and every subsequent
 * balance shift. The parent regenerates and passes fresh rows down, so the
 * change is visible immediately rather than after saving.
 */
export const PlanScheduleTable: React.FC<PlanScheduleTableProps> = ({
  payments,
  breachDayKeys,
  floorByMonth,
  threshold = 0,
  onOverride,
}) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>('');

  const commit = (dayKey: string) => {
    const value = parseFloat(draft);
    onOverride(dayKey, Number.isFinite(value) && value >= 0 ? value : null);
    setEditingKey(null);
  };

  if (payments.length === 0) {
    return <EmptyState>No payments to show</EmptyState>;
  }

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const totalInterest = payments.reduce((sum, p) => sum + p.interestAccrued, 0);

  const TH = cn(TYPE.sectionLabel, 'text-ink-muted py-2.5 px-3 text-left bg-ground-sunk');

  return (
    <div className="bg-surface border border-rule-base rounded-xl overflow-hidden">
      {/* Vertical scroller only — the two widest columns drop below sm rather
          than forcing a horizontal scroll. sticky works here because this is
          the scroll container itself, not an ancestor of it. */}
      <div className="max-h-[420px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-rule-base">
              <th className={TH}>Date</th>
              <th className={TH}>Type</th>
              <th className={cn(TH, 'text-right')}>Payment</th>
              <th className={cn(TH, 'text-right hidden sm:table-cell')}>Interest</th>
              <th className={cn(TH, 'text-right hidden sm:table-cell')}>Principal</th>
              <th className={cn(TH, 'text-right')}>Remaining</th>
              {floorByMonth && (
                <th className={cn(TH, 'text-right')} title="Lowest projected checking balance that month">
                  Floor that month
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {payments.map(p => {
              const key = toDayKey(p.dueDate);
              const isEditing = editingKey === key;
              const breaches = breachDayKeys?.has(key);
              return (
                <tr
                  key={`${key}-${p.sequence}`}
                  className={cn(
                    'border-b border-rule last:border-b-0',
                    // Ternary: two competing border-l colours would be resolved
                    // by stylesheet order, not by the order written here.
                    breaches ? 'border-l-2 border-l-warn' : 'border-l-2 border-l-transparent'
                  )}
                >
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className={cn(TYPE.metaTight, breaches ? 'text-warn' : 'text-ink-soft')}>
                      {p.dueDate.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    {breaches && (
                      <span
                        className={cn(TYPE.metaTight, 'text-warn ml-2')}
                        title="Projected checking balance is below your threshold on this day"
                      >
                        Low
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Tag weight={p.kind === 'FINAL' ? 'ink' : 'bone'}>
                        {p.kind === 'EXTRA' ? 'Extra' : p.kind === 'FINAL' ? 'Final' : 'Scheduled'}
                      </Tag>
                      {p.isCustomized && (
                        <span
                          className={cn(TYPE.metaTight, 'text-ink-muted')}
                          title={`Generated: ${usd(p.generatedAmount)}`}
                        >
                          Edited
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    {isEditing ? (
                      <input
                        autoFocus
                        type="number"
                        step="0.01"
                        min="0"
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={() => commit(key)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') commit(key);
                          if (e.key === 'Escape') setEditingKey(null);
                        }}
                        className="w-24 px-2 py-1 bg-transparent text-right text-ink border-0 border-b-2 border-accent focus:outline-none"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          if (p.kind === 'EXTRA') return;
                          setEditingKey(key);
                          setDraft(String(p.amount));
                        }}
                        disabled={p.kind === 'EXTRA'}
                        className={cn(
                          'text-[13.5px] font-semibold tabular-nums transition-colors duration-[120ms]',
                          p.kind === 'EXTRA'
                            ? 'text-ink-soft cursor-default'
                            : 'text-ink border-b border-rule-strong hover:text-accent hover:border-accent'
                        )}
                        title={p.kind === 'EXTRA' ? undefined : 'Click to change this month'}
                      >
                        {usd(p.amount)}
                      </button>
                    )}
                    {p.isCustomized && !isEditing && (
                      <button
                        type="button"
                        onClick={() => onOverride(key, null)}
                        className="ml-2 text-ink-muted hover:text-bad transition-colors duration-[120ms]"
                        title="Revert to the generated amount"
                        aria-label="Revert to the generated amount"
                      >
                        ↺
                      </button>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right hidden sm:table-cell">
                    <span className={cn(TYPE.metaTight, 'text-ink-muted')}>
                      {p.interestAccrued > 0 ? usd(p.interestAccrued) : '—'}
                    </span>
                  </td>
                  <td
                    className="py-2 px-3 text-right hidden sm:table-cell"
                    title={
                      p.principalApplied < 0
                        ? 'Negative amortization — this payment does not cover the interest, so the balance grows'
                        : undefined
                    }
                  >
                    <span className={cn(TYPE.metaTight, p.principalApplied < 0 ? 'text-bad' : 'text-ink-muted')}>
                      {usd(p.principalApplied)}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <span className="text-[13.5px] font-semibold tabular-nums text-ink">
                      {usd(p.closingBalance)}
                    </span>
                  </td>
                  {floorByMonth && (() => {
                    const key = `${p.dueDate.getFullYear()}-${String(p.dueDate.getMonth() + 1).padStart(2, '0')}`;
                    const floor = floorByMonth.get(key);
                    if (floor === undefined) {
                      return <td className="py-2 px-3 text-right"><span className={cn(TYPE.meta, 'text-ink-muted')}>—</span></td>;
                    }
                    // Under the floor is approaching trouble; under zero is
                    // overdrawn. The underline carries it without the colour.
                    const tone =
                      floor < 0
                        ? 'text-bad'
                        : threshold > 0 && floor < threshold
                          ? 'text-warn border-b border-warn'
                          : 'text-ink-muted';
                    return (
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <span className={cn('text-[13px] font-semibold tabular-nums', tone)}>
                          {usd(floor)}
                        </span>
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-3 rule-total flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2">
        <span className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
          Totals over {payments.length} payments
        </span>
        <span className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <span className="flex items-baseline gap-2">
            <span className={cn(TYPE.meta, 'text-ink-muted')}>paid</span>
            <span className={cn(TYPE.rowFigure, 'text-ink')}>{usd(totalPaid)}</span>
          </span>
          <span className="flex items-baseline gap-2">
            <span className={cn(TYPE.meta, 'text-ink-muted')}>interest</span>
            <span className={cn(TYPE.rowFigure, 'text-warn')}>{usd(totalInterest)}</span>
          </span>
        </span>
      </div>
      <p className={cn(TYPE.metaTight, 'text-ink-muted px-3 py-2.5 border-t border-rule')}>
        Click any scheduled amount to change that month — later payments re-solve automatically
      </p>
    </div>
  );
};
